import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {
    MvsWidgetFormGenericEventConstants
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {
    MvsFormValueListEntryDto
} from 'features/core/shared/dto/form/dto/mvs-form-value-list-entry.dto';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';
import {CpProvisionContractAgentService} from '../../../../../service/api/cp-provision-contract-agent.service';
import {
    ProvisionAgentChangeConsequenceDto,
    ProvisionAgentChangeRequestDto,
    ProvisionAgentChangeResultDto
} from '../../../../../model/dto/api/provision-agent-change.dto';
import {ProvisionAgentDto} from '../../../../../model/dto/entity/provision-agent.dto';
import {CpFeatureRight} from '../../../../../service/domain/cp-feature-right';

/**
 * Action dialog "Berater wechseln" (04-frontend.md §7)
 * (`GET /mvsa/cp/provisionContractAgents/{id}/changeAgent/preview` +
 * `POST /mvsa/cp/provisionContractAgents/{id}/changeAgent`; um right `cp_agentChange`, AX-08 adds
 * no new right).
 *
 * ── WHAT MOVED IN WP-E14 (and why)
 *
 * The write target is now the PROVISIONSSATZ `cp.ProvisionContractAgent` (R-08), not the contract
 * event `cp.ProvisionContract` (R-07). `provisionAgent` moved off R-07 onto R-08 in WP-E5 (A6-04):
 * a contract event fans out into one rate row per Provisionsart and per Berater, so "the agent of
 * this contract" is no longer a thing that exists — only "the Berater of this Satz" is. §7 states
 * the consequence for this dialog explicitly: it "muss den Satz auswählen lassen (Vertrag ×
 * Ereignis × Provisionsart)". That selection is made in the hosting pane
 * {@link CpProvisionAgentContractAgentsComponent}, whose table IS the list of Sätze; this dialog
 * receives the chosen id and shows its label next to the amounts it is about to redirect.
 *
 * The endpoint is the ONLY legal write path for `ProvisionContractAgent.provisionAgent` (the
 * generic form keeps the field readonly). Only the new agent plus the mandatory reason is written,
 * and `agentManuallyChanged` is set to true; the bookings are only created by the next calculation
 * run (reversals for the old agent, new bookings for the new one, original valuta, re-cap of the
 * reserve — example E8).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * FORM MECHANISM USED (design gate finding B-9, capability-matrix.md §5 — MANDATORY
 * DOCUMENTATION): TRANSIENT FORM WIDGET built from an `MvsFormDto` constructed in the frontend,
 * IDENTICAL to the two sibling dialogs
 * {@link CpProvisionAgentManualBookingDialogComponent} and
 * {@link CpProvisionAgentTransferDialogComponent}:
 *
 *   const form = new MvsFormDto();  form.formFields = { … MvsFormFieldDto … };
 *   WidgetFactory.createWidget(alias, name, 'form', 'transient', 'transient', objectType,
 *                              WidgetDataParam.create('form', form));
 *   <mvs-widget [widgetData]="…" (onGenericEvent)="…">
 *
 * The framework renders the fields (`mvs-widget-form` -> `mvs-form` -> the registered
 * input field components); the captured values come back via
 * `MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE` together with `MvsFormGroup.status`.
 * This component contains NO hand-written form HTML — the template only holds the
 * `p-dialog` frame, the consequences preview, and the action buttons.
 *
 * WHY NOT `MvsUiObjectService.openObjectViaDialog(...)` / `mvs-crud-object`: both write
 * via the generic CRUD of the object type. That is exactly what is forbidden here — `provisionAgent`
 * may ONLY be set via `/changeAgent`, because only this path enforces the mandatory reason and
 * triggers the reconcile case. Also, `reason` does not exist in any table: it is a pure
 * request field for which there is no backend FormObject.
 * REPO EVIDENCE for exactly this pattern: `features/feature-core/wf/component/protected-components/
 * wf-process-start/wf-process-start.component.ts` (start parameters of a process, likewise without
 * an entity behind the form).
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * MANDATORY PREVIEW (01 §7, BR-016): the submit button stays locked until
 * {@link previewChangeAgent} has responded for the SELECTED target agent. The preview is
 * the only place where the caseworker sees, BEFORE writing, which active bookings will be
 * reversed and rebuilt under the new agent. If already PAID-OUT bookings are
 * affected (`affectsPaidOutBookings`), 01 §7 additionally requires an explicit
 * confirmation — handled via the shared PrimeNG `ConfirmationService` (host
 * `<p-confirmdialog>` in `app.main.component.html`), the same construction as in
 * {@link CpProvisionRunStatusFlowComponent}. Deliberately NO own checkbox: that would be a
 * hand-written form field.
 *
 * PERMISSION (U-08): {@link canAgentChange} reflects the um feature right `cp_agentChange` and
 * defaults to TRUE (optimistic) — a DOCUMENTED DEVIATION from 04-frontend.md §5.1 ("Alle Buttons
 * nur mit um-Recht aktiv"), applied identically to all four cp rights by orchestrator decision
 * OD-1. The reasoning is kept in ONE place, {@link CpFeatureRight}; in short: this frontend has no
 * feature-rights service and no cp payload carries a grant, so a `false` default would never be
 * raised by anything and the submit button would be permanently inert. That loses the function
 * without closing a hole — the security boundary is `cp/cross/um/ProvisionFeatureGuard`, itself
 * default-ALLOW, which answers a genuine refusal with HTTP 403.
 *
 * An earlier version of this dialog kept the stricter default with the argument that an agent
 * change retroactively redirects money flows. That argument is real but it is an argument for a
 * CONFIRMATION, not for a rights default — and the confirmation exists: the mandatory preview
 * plus the explicit paid-out confirmation below. A dead button protects nobody.
 *
 * Both 403 paths (preview and write) run through {@link demoteOnForbidden}: the flag is demoted
 * AND {@link onForbidden} is emitted so the hosting pane locks its entry button too.
 */
@Component({
    selector: 'cp-provision-agent-change-dialog',
    templateUrl: './cp-provision-agent-change-dialog.component.html',
    styleUrls: ['./cp-provision-agent-change-dialog.component.scss'],
    standalone: false
})
export class CpProvisionAgentChangeDialogComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    /** The Provisionssatz whose Berater is being changed (`cp.ProvisionContractAgent`). */
    @Input() provisionContractAgentId: number;

    /** Display text of the Satz (Vertrag · Ereignis · Provisionsart); purely informational. */
    @Input() provisionContractAgentLabel: string;

    /** Current Abrechnungskonto of the Satz — excluded from the selection list. */
    @Input() currentProvisionAgentId: number;

    /** Display name of the current agent; purely informational. */
    @Input() currentProvisionAgentName: string;

    /** um right `cp_agentChange` (U-08) — optimistic default, demoted on 403; see class Javadoc. */
    @Input() canAgentChange: boolean = true;

    /** Visibility of the dialog; intended as a two-way binding (`[(visible)]`). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Fires after a successfully written agent change. */
    @Output() onAgentChanged = new EventEmitter<ProvisionAgentChangeResultDto>();

    /**
     * Fires when the server refused the right with a 403 — the demotion signal of the module-wide
     * optimistic pattern. The hosting pane uses it to lock its own entry button, so the user is
     * not sent back into a dialog the server will refuse again.
     */
    @Output() onForbidden = new EventEmitter<void>();

    /** The transient capture widget (see class Javadoc). */
    formWidget: WidgetData;

    /** Last VALID form value; null as long as the form is incomplete/invalid. */
    formValue: any = null;

    /** Selectable target agents (active, excluding the current one). */
    targetAgents: ProvisionAgentDto[] = [];

    /** The target agent the preview applies to; null as long as none is selected. */
    previewAgentId: number = null;

    /** State of the mandatory preview. */
    previewState: 'idle' | 'loading' | 'loaded' | 'failed' = 'idle';

    /** The consequences of the change per the preview (E8). */
    consequences: ProvisionAgentChangeConsequenceDto[] = [];

    /** Backend error message when `success = false`, or on a technical error. */
    failureMessage: string = null;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected provisionContractAgentService: CpProvisionContractAgentService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.initialized = true;
        if (this.visible) {
            this.refreshComponent();
        }
    }

    /**
     * Rebuilds the form and preview fresh on every open. A stale form value or a preview of the
     * previously viewed Satz would be especially dangerous here: the dialog is opened from a list
     * whose selection changes between openings.
     */
    refreshComponent(): void {

        this.formValue = null;
        this.failureMessage = null;
        this.formWidget = null;
        this.consequences = [];
        this.previewAgentId = null;
        this.previewState = 'idle';

        if (!this.provisionContractAgentId) {
            return;
        }

        this.busy = true;
        this.loadTargetAgents();
    }

    /**
     * Loads the selectable target agents and builds the transient form from them.
     *
     * Why a self-loaded value list and NOT an FK field on `cp.ProvisionAgent`: the current
     * agent must disappear from the selection (a change to oneself is not a change),
     * and the preview needs the target agent's name in plain text. A generic FK field
     * cannot deliver either. The sibling dialog
     * {@link CpProvisionAgentManualBookingDialogComponent} uses the same construction for its
     * booking types.
     */
    private loadTargetAgents(): void {

        const filterCriteria = [
            FilterCriteria.create('entityStatus', FilterCriteria.cOperatorEqual, EntityStatusEnum.active)
        ];

        const objectRequestList = ObjectRequestList.createBasic(false, filterCriteria, null);

        this.coreService.getCrudService('cp.ProvisionAgent')
            .list(objectRequestList)
            .subscribe({
                next: (dtoList) => {
                    const entries = (dtoList?.entries as ProvisionAgentDto[]) ?? [];
                    this.targetAgents = entries.filter(
                        agent => !this.currentProvisionAgentId || agent.id != this.currentProvisionAgentId);
                    this.buildFormWidget();
                    this.busy = false;
                },
                error: () => {
                    this.targetAgents = [];
                    this.buildFormWidget();
                    this.busy = false;
                }
            });
    }

    /**
     * Builds the `MvsFormDto` of the agent change and the transient form widget from it.
     * The field ids match the attributes of {@link ProvisionAgentChangeRequestDto} 1:1,
     * so {@link handleChangeAgent} needs no renaming logic.
     */
    private buildFormWidget(): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        // New agent — MANDATORY FIELD (01 §7 "selection of the new agent").
        const newAgentField = MvsFormFieldDto.createBasic(
            'newProvisionAgentId', 'select', 'Neuer Berater');
        newAgentField.uiMandatory = true;
        newAgentField.order = 10;
        newAgentField.uiDescription =
            'Nur aktive Abrechnungskonten; das aktuelle Konto dieses Satzes steht nicht zur Auswahl.';
        newAgentField.createValueList(
            ...this.targetAgents.map(agent =>
                MvsFormValueListEntryDto.createBasic(agent.id, agent.agentDtoName ?? agent.name)));
        formFields['newProvisionAgentId'] = newAgentField;

        // Reason — MANDATORY FIELD (03 §11.1 "mandatory reason", 01 §7).
        const reasonField = MvsFormFieldDto.createBasic('reason', 'textarea', 'Grund');
        reasonField.uiMandatory = true;
        reasonField.uiDescription = 'Pflichtangabe: warum wechselt dieser Provisionssatz den Berater?';
        reasonField.order = 20;
        formFields['reason'] = reasonField;

        form.formFields = formFields;

        /**
         * AI: Transient form widget for capturing an agent change on a Provisionssatz.
         *     Source: MvsFormDto built in the frontend (reason exists in no table) — the
         *     write goes to POST /cp/provisionContractAgents/{id}/changeAgent.
         *     Caseworkers use it to reassign one commission rate row to a different Berater.
         */
        this.formWidget = WidgetFactory.createWidget(
            'cp.agent.agentChange.form',
            'Berater wechseln',
            'form',
            'transient',
            'transient',
            'cp.ProvisionContractAgent',
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('form', form)
        );
    }

    /**
     * Receives the form values.
     *
     * Two things happen here deliberately separated: {@link formValue} is set ONLY when the
     * form is fully valid (otherwise the submit button stays locked), while the PREVIEW
     * depends solely on the selected target agent. Otherwise the caseworker would only see the
     * consequences after typing the reason — i.e. exactly not when they need them for the
     * decision.
     */
    handleGenericEvent(event: WidgetGenericEvent): void {

        if (event.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return;
        }

        const formGroup: MvsFormGroup =
            event.object[MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP];

        this.formValue = formGroup.status === 'VALID' ? formGroup.value : null;
        this.failureMessage = null;

        const rawAgentId = formGroup.value?.['newProvisionAgentId'];
        const newAgentId = rawAgentId ? Number(rawAgentId) : null;

        if (newAgentId !== this.previewAgentId) {
            this.loadPreview(newAgentId);
        }
    }

    /**
     * Mandatory preview of the reconcile consequences (E8, BR-016) — read-only, no commit.
     *
     * If it fails, {@link previewState} stays `failed` and the submit button stays locked:
     * without having seen the consequences, an agent change must not be submitted, because it
     * can retroactively turn already paid-out amounts into open receivables (03 §5.7 step 4).
     */
    private loadPreview(newProvisionAgentId: number): void {

        this.previewAgentId = newProvisionAgentId;
        this.consequences = [];

        if (!newProvisionAgentId || !this.provisionContractAgentId) {
            this.previewState = 'idle';
            return;
        }

        this.previewState = 'loading';

        this.provisionContractAgentService.previewChangeAgent(this.provisionContractAgentId, newProvisionAgentId)
            .subscribe({
                next: (consequences) => {
                    // An empty result is a valid statement ("no active bookings
                    // affected") and not a missing preview.
                    this.consequences = consequences ?? [];
                    this.previewState = 'loaded';
                },
                error: (error) => {
                    this.previewState = 'failed';
                    this.demoteOnForbidden(error);
                    this.failureMessage = CpFeatureRight.isForbidden(error)
                        ? CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE)
                        : 'Die Vorschau der Folgen konnte nicht geladen werden. '
                        + 'Ohne Vorschau kann der Wechsel nicht abgeschickt werden.';
                }
            });
    }

    /** Name of the selected target agent (for preview text and confirmation dialog). */
    get targetAgentName(): string {
        if (!this.previewAgentId) {
            return null;
        }
        const agent = this.targetAgents.find(entry => entry.id == this.previewAgentId);
        return agent ? (agent.agentDtoName ?? agent.name) : null;
    }

    /** Sum of affected rows across all consequences — the order of magnitude at a glance. */
    get affectedRowCount(): number {
        return this.consequences.reduce((sum, entry) => sum + (entry.affectedCount ?? 0), 0);
    }

    /**
     * True if already PAID-OUT bookings are affected. Then 01 §7 requires an
     * explicit confirmation — this leaves the previous agent with an open receivable
     * (BR-079, 03 §5.7 step 4).
     */
    get affectsPaidOutBookings(): boolean {
        return this.consequences.some(entry => entry.affectsPaidOutBookings === true);
    }

    /**
     * The submit button is only active when ALL four conditions are met: right granted,
     * form fully valid, preview loaded for exactly this target agent, no
     * request in flight.
     */
    get submittable(): boolean {
        return this.canAgentChange
            && !!this.formValue
            && this.previewState === 'loaded'
            && !this.busy;
    }

    /**
     * Why the submit button is locked, in German — one reason per cause, in the order the
     * caseworker meets them. Collapsing them into a single "Erfordert das Recht … und die
     * Vorschau" would name the right even when the right is not the problem, which is exactly the
     * misleading text the optimistic pattern is meant to remove (see the PERMISSION section of the
     * class TSDoc).
     */
    get submitTooltip(): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (!this.formValue) {
            return 'Bitte neuen Berater auswählen und einen Grund angeben.';
        }

        if (this.previewState !== 'loaded') {
            return 'Die Vorschau der Folgen muss geladen sein, bevor der Wechsel abgeschickt '
                + 'werden kann.';
        }

        return 'Wechsel abschicken.';
    }

    /**
     * Submits. If paid-out bookings are affected, first the explicit confirmation
     * (01 §7) via the shared `ConfirmationService`, otherwise directly.
     */
    handleChangeAgent(): void {

        if (!this.submittable) {
            return;
        }

        if (!this.affectsPaidOutBookings) {
            this.executeChangeAgent();
            return;
        }

        // DOCUMENTED DEVIATION from UX7 (Font Awesome only): ConfirmationService.confirm()'s
        // `icon` renders through PrimeNG's own ConfirmDialog template, which expects a `pi`
        // class there — the bm reference module keeps `pi pi-*` for every confirm() call for
        // exactly that reason (e.g. bm-billing-run-status-flow.component.ts:181). No other icon
        // in this component uses PrimeIcons.
        this.confirmationService.confirm({
            header: 'Bereits ausgezahlte Buchungen betroffen',
            message: 'Der Wechsel betrifft Buchungen, die bereits ausgezahlt wurden. Beim bisherigen '
                + 'Berater entsteht dadurch eine offene Forderung, die mit künftigen Läufen '
                + 'verrechnet wird. Wechsel trotzdem durchführen?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Wechsel durchführen',
            rejectLabel: 'Abbrechen',
            accept: () => this.executeChangeAgent()
        });
    }

    /**
     * Writes the change to the custom endpoint.
     *
     * A business failure comes back as HTTP 200 with `success = false` (bm convention) —
     * so `success` is checked on EVERY response, and 2xx is never assumed to mean
     * "changed".
     */
    private executeChangeAgent(): void {

        const request: ProvisionAgentChangeRequestDto = {
            newProvisionAgentId: Number(this.formValue['newProvisionAgentId']),
            reason: this.formValue['reason']
        };

        this.busy = true;
        this.failureMessage = null;

        this.provisionContractAgentService.changeAgent(this.provisionContractAgentId, request).subscribe({
            next: (result: ProvisionAgentChangeResultDto) => {
                this.busy = false;

                if (!result || result.success !== true) {
                    this.failureMessage = result?.failureMessage
                        ?? 'Der Beraterwechsel konnte nicht ausgeführt werden.';
                    return;
                }

                this.messageService.showSuccessMessage(
                    'Berater gewechselt',
                    'Die Buchungen werden mit dem nächsten Berechnungslauf umgehängt.');

                this.informObjectChanged();
                this.onAgentChanged.emit(result);
                this.handleClose();
            },
            error: (error) => {
                this.busy = false;
                this.demoteOnForbidden(error);
                this.failureMessage = CpFeatureRight.isForbidden(error)
                    ? CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE)
                    : 'Der Beraterwechsel konnte nicht ausgeführt werden. Bitte Eingaben prüfen.';
            }
        });
    }

    /**
     * A 403 is the um right `cp_agentChange` responding — the demotion half of the module-wide
     * optimistic pattern (class TSDoc, {@link CpFeatureRight}). The mirrored flag is demoted so
     * the dialog no longer offers an action the server refuses, and the host is told so it can
     * lock the entry button that led here.
     *
     * ONLY a 403 demotes: a 500 or a dropped connection must leave the flag alone, otherwise a
     * transient backend fault would silently take the action away from a user who holds the right.
     */
    private demoteOnForbidden(error: any): void {
        if (CpFeatureRight.isForbidden(error)) {
            this.canAgentChange = false;
            this.onForbidden.emit();
        }
    }

    /**
     * Informs all listeners (rate list, object pages) about the changed Provisionssatz.
     * Same channel the generic CRUD uses — existing subscribers react without
     * extra wiring. The account bookings do NOT change immediately (only in the next
     * run), so deliberately only `cp.ProvisionContractAgent` is reported here. `before`/`after`
     * stay null as in the two sibling dialogs — the signature takes DtoDetail objects, not ids,
     * and the subscribers in the module filter exclusively on `objectType`.
     */
    private informObjectChanged(): void {
        this.mvsObjectService.objectChangeEvent.next(
            ObjectChangeInformation.create(
                'cp.ProvisionContractAgent',
                ObjectChangeInformationActionEnum.updated,
                null,
                null));
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['visible'] && this.visible) {
            this.refreshComponent();
        }
        if (changes['provisionContractAgentId'] && this.visible) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

}
